// ── 데이터 관리: 초기화 · 시즌 보관함 · 정산 리포트 ───────────────
// 초기화 = 기록 삭제가 아니라 "시즌 보관함으로 이동". 영구 보관, 언제든 복원 가능.
// 실제 이동은 서버 함수(functions/index.js의 resetRecords 등)가 처리함.
// 보관함 기록은 메인 기록/월별 리포트에 섞이지 않음 (보관함 화면에서만 계산).

import React, { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import { INCOME_CATS, EXPENSE_CATS, summarize } from "./reportEngine";
import { MonthlyReport, Donut, Legend, short, ACCENT, UP, DOWN } from "./MonthlyReport";

const call = (name, data) => httpsCallable(functions, name)(data).then(r => r.data);

const WARN = "#E8A33D";
const WARN_SOFT = "rgba(232,163,61,0.12)";

const cardStyle = (T, pad = 20) => ({ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: pad });
const titleStyle = T => ({ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 });
const btn = (bg, color = "#fff", extra = {}) => ({ padding: "9px 16px", borderRadius: 9, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", background: bg, color, ...extra });

// "YYYY-MM-DD" 두 날짜 사이 일수 (양 끝 포함). 타임존 영향 없게 UTC 기준으로 계산
function spanDays(a, b) {
  const p = s => { const [y, m, d] = s.split("-").map(Number); return Date.UTC(y, m - 1, d); };
  return Math.max(1, Math.round((p(b) - p(a)) / 864e5) + 1);
}
function msToDate(ms) {
  if (!ms) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
// 서버(resetRecords)와 같은 규칙: 기간의 끝은 오늘(한국 날짜)을 넘지 않음
function rangeOf(scope, year, month) {
  const today = msToDate(Date.now());
  const cap = r => ({ ...r, to: r.to > today ? today : r.to });
  if (scope === "all") return cap({ from: "0000-00-00", to: "9999-99-99", label: "전체 기록" });
  if (scope === "year") return cap({ from: `${year}-01-01`, to: `${year}-12-31`, label: `${year}년` });
  const mm = String(month).padStart(2, "0");
  return cap({ from: `${year}-${mm}-01`, to: `${year}-${mm}-31`, label: `${year}년 ${Number(month)}월` });
}
const inRange = (r, from, to) => r.date && r.date >= from && r.date <= to;

// ── 공통 모달 ──
function Modal({ T, title, onClose, children, width = 560 }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 12px", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ ...cardStyle(T, 22), width: "100%", maxWidth: width, boxShadow: "0 12px 40px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{title}</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: T.sub, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── 시즌 정산 리포트 ──
function SeasonReport({ season, prevSeason, T, isMobile, fmt, favorites, calcIncome, calcSolIncome, onClose }) {
  const [recs, setRecs] = useState(null);
  const [prevRecs, setPrevRecs] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    call("getSeasonRecords", { seasonId: season.id }).then(r => alive && setRecs(r)).catch(e => alive && setErr(e.message));
    if (prevSeason) call("getSeasonRecords", { seasonId: prevSeason.id }).then(r => alive && setPrevRecs(r)).catch(() => {});
    return () => { alive = false; };
  }, [season.id, prevSeason]);

  const ctx = { favorites, includeSol: true, calcIncome, calcSolIncome };
  const cur = useMemo(() => (recs ? summarize(recs, ctx) : null), [recs]); // eslint-disable-line react-hooks/exhaustive-deps
  const prev = useMemo(() => (prevRecs ? summarize(prevRecs, ctx) : null), [prevRecs]); // eslint-disable-line react-hooks/exhaustive-deps

  const days = spanDays(season.firstDate, season.lastDate);
  const perDay = cur ? cur.farmIncome / days : 0;
  const prevDays = prevSeason ? spanDays(prevSeason.firstDate, prevSeason.lastDate) : 0;
  const prevPerDay = prev ? prev.farmIncome / prevDays : 0;
  const pct = prev && prevPerDay > 0 ? ((perDay - prevPerDay) / prevPerDay) * 100 : null;
  const edited = season.updatedAt && season.createdAt && season.updatedAt - season.createdAt > 60000;

  return (
    <Modal T={T} title={`📦 ${season.name} 정산 리포트`} onClose={onClose} width={860}>
      <div style={{ fontSize: 13, color: T.sub, marginBottom: 16, lineHeight: 1.6 }}>
        {season.label} · {season.firstDate} ~ {season.lastDate} ({days}일) · 기록 {season.count}건
        {edited && <span style={{ color: WARN }}> · 마지막 수정 {msToDate(season.updatedAt)}</span>}
      </div>

      {err && <div style={{ color: DOWN, fontSize: 13 }}>불러오기 실패: {err}</div>}
      {!cur && !err && <div style={{ color: T.sub, fontSize: 13, padding: 20, textAlign: "center" }}>정산 중...</div>}

      {cur && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* 핵심 숫자 */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: 10 }}>
            {[["파밍 수익", cur.farmIncome, UP, true], ["지출", cur.totalExpense, DOWN], ["순이익", cur.net, cur.net >= 0 ? ACCENT : DOWN]].map(([l, v, c, main]) => (
              <div key={l} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14, gridColumn: isMobile && main ? "1 / -1" : "auto" }}>
                <div style={{ fontSize: 12, color: T.sub }}>{l}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: c, marginTop: 6 }}>{fmt(v)}</div>
              </div>
            ))}
          </div>

          {/* 이전 시즌 대비 (일평균 기준) */}
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 10 }}>📈 하루 평균 파밍 수익</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{fmt(perDay)}</span>
              {pct != null && <span style={{ fontSize: 13, fontWeight: 600, color: pct >= 0 ? UP : DOWN }}>{pct >= 0 ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}%</span>}
            </div>
            <div style={{ fontSize: 12, color: T.sub, marginTop: 6, lineHeight: 1.6 }}>
              {prevSeason
                ? prev ? `${prevSeason.name}(하루 평균 ${fmt(prevPerDay)}, 총 ${short(prev.farmIncome)}) 대비. 시즌마다 기간이 달라서 하루 평균으로 비교해요.` : "이전 시즌 불러오는 중..."
                : "첫 시즌이에요. 다음 시즌부터 이전 시즌과 비교해드려요."}
            </div>
          </div>

          {/* 구성 */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
            {[["수익 구성", INCOME_CATS, cur.income, cur.farmIncome, prev?.income], ["지출 구성", EXPENSE_CATS, cur.expense, cur.totalExpense, prev?.expense]].map(([t, cats, map, total, pm]) => {
              const items = cats.map(c => ({ ...c, value: map[c.key] }));
              return (
                <div key={t} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>{t}</div>
                  <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                    <Donut items={items} T={T} size={118} thickness={14} label={t.slice(0, 2)} value={short(total)} />
                    <Legend items={items} total={total} T={T} prevMap={pm} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* 시즌 안 월별 흐름 */}
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginTop: 6 }}>🗓️ 이 시즌의 월별 기록</div>
          <MonthlyReport records={recs} favorites={favorites} T={T} isMobile={isMobile} fmt={fmt} calcIncome={calcIncome} calcSolIncome={calcSolIncome} />
        </div>
      )}
    </Modal>
  );
}

// ── 데이터 관리 본체 ──
export default function DataManager({ records, favorites, T, isMobile, fmt, calcIncome, calcSolIncome, showToast }) {
  const years = useMemo(() => [...new Set(records.map(r => r.date?.slice(0, 4)).filter(Boolean))].sort().reverse(), [records]);
  const [scope, setScope] = useState("month");
  const [year, setYear] = useState(years[0] || "");
  const months = useMemo(() => [...new Set(records.filter(r => r.date?.startsWith(year)).map(r => r.date.slice(5, 7)))].sort().reverse(), [records, year]);
  const [month, setMonth] = useState("");
  useEffect(() => { if (!year && years[0]) setYear(years[0]); }, [years, year]);
  useEffect(() => { if (!months.includes(month)) setMonth(months[0] || ""); }, [months, month]);

  const range = rangeOf(scope, year, month);
  const target = useMemo(() => records.filter(r => inRange(r, range.from, range.to)).map(r => r.date).sort(), [records, range.from, range.to]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [agree, setAgree] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState("");
  const [seasons, setSeasons] = useState(null);
  const [report, setReport] = useState(null);
  const [seasonErr, setSeasonErr] = useState("");

  const loadSeasons = () => call("listSeasons").then(s => { setSeasons(s); setSeasonErr(""); return s; }).catch(e => { setSeasonErr(e.message); setSeasons([]); return []; });
  useEffect(() => { loadSeasons(); }, []);

  const doReset = async () => {
    setBusy("reset");
    try {
      const res = await call("resetRecords", { scope, year, month });
      setConfirmOpen(false); setAgree(false); setTyped("");
      showToast?.(`${res.moved}건을 시즌 보관함으로 옮겼어요`, "📦");
      const list = await loadSeasons();
      const s = list.find(x => x.id === res.seasonId);
      if (s) setReport(s);
    } catch (e) { alert("초기화 실패: " + e.message); }
    setBusy("");
  };
  const doMerge = async s => {
    const n = records.filter(r => inRange(r, s.from, s.to)).length;
    if (!window.confirm(`현재 기록 중 ${s.name} 기간(${s.label})에 해당하는 ${n}건을 ${s.name}에 합칠까요?\n합친 기록은 메인 기록과 월별 리포트에서 빠지고, ${s.name} 정산 리포트에 반영돼요.`)) return;
    setBusy("merge:" + s.id);
    try { const r = await call("resetRecords", { seasonId: s.id }); showToast?.(`${r.moved}건을 ${s.name}에 합쳤어요`, "📦"); await loadSeasons(); }
    catch (e) { alert("합치기 실패: " + e.message); }
    setBusy("");
  };
  const doRestore = async s => {
    if (!window.confirm(`${s.name}(${s.count}건)을 현재 기록으로 되돌릴까요?\n되돌린 기록은 다시 월별 리포트와 통계에 포함되고, 이 시즌은 보관함에서 사라져요.`)) return;
    setBusy("restore:" + s.id);
    try { const r = await call("restoreSeason", { seasonId: s.id }); showToast?.(`${r.restored}건을 되돌렸어요`, "↩️"); await loadSeasons(); }
    catch (e) { alert("복원 실패: " + e.message); }
    setBusy("");
  };

  const pill = (k, l) => (
    <button key={k} onClick={() => setScope(k)} style={{ padding: "7px 16px", borderRadius: 20, fontSize: 13, cursor: "pointer", fontWeight: scope === k ? 600 : 400,
      border: `1px solid ${scope === k ? ACCENT : T.border}`, background: scope === k ? "rgba(91,79,208,0.16)" : "transparent", color: scope === k ? T.text : T.sub }}>{l}</button>
  );
  const sel = { background: T.inp || T.bg, color: T.text, border: `1px solid ${T.inpBdr || T.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13 };
  const canConfirm = agree && typed.trim() === "초기화" && busy !== "reset";

  return (
    <div style={{ ...cardStyle(T, isMobile ? 16 : 20), marginBottom: 10 }}>
      <div style={titleStyle(T)}>🗂️ 데이터 관리</div>
      <div style={{ fontSize: 12, color: T.sub, lineHeight: 1.6, marginBottom: 16 }}>
        새 가계부처럼 다시 시작하고 싶을 때 초기화하세요. 기록은 지워지지 않고 <b style={{ color: T.text }}>시즌 보관함에 영구 보관</b>되며, 언제든 되돌릴 수 있어요.
      </div>

      {/* 초기화 */}
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>🔄 초기화</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>{pill("month", "월 단위")}{pill("year", "연 단위")}{pill("all", "전체")}</div>
        {scope !== "all" && (
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <select value={year} onChange={e => setYear(e.target.value)} style={sel}>{years.map(y => <option key={y} value={y}>{y}년</option>)}</select>
            {scope === "month" && <select value={month} onChange={e => setMonth(e.target.value)} style={sel}>{months.map(m => <option key={m} value={m}>{Number(m)}월</option>)}</select>}
          </div>
        )}
        <div style={{ fontSize: 13, color: T.text, marginBottom: 12 }}>
          {target.length > 0
            ? <>옮겨질 기록: <b>{target.length}건</b> <span style={{ color: T.sub }}>({target[0]} ~ {target[target.length - 1]})</span></>
            : <span style={{ color: T.sub }}>선택한 기간에 기록이 없어요.</span>}
        </div>
        <button disabled={!target.length} onClick={() => setConfirmOpen(true)}
          style={btn(target.length ? DOWN : T.border, "#fff", { opacity: target.length ? 1 : 0.6, cursor: target.length ? "pointer" : "not-allowed" })}>
          {range.label} 초기화하기
        </button>
      </div>

      {/* 시즌 보관함 */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 8 }}>📦 시즌 보관함</div>
        {seasons === null && <div style={{ fontSize: 13, color: T.sub }}>불러오는 중...</div>}
        {seasonErr && <div style={{ fontSize: 12, color: DOWN, marginBottom: 6 }}>보관함을 불러오지 못했어요: {seasonErr}</div>}
        {seasons && seasons.length === 0 && !seasonErr && <div style={{ fontSize: 13, color: T.sub }}>아직 보관된 시즌이 없어요.</div>}
        {seasons && seasons.map((s, i) => {
          const late = records.filter(r => inRange(r, s.from, s.to)).length;
          return (
            <div key={s.id} style={{ borderTop: i ? `1px solid ${T.border}` : "none", padding: "12px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{s.name} <span style={{ fontSize: 12, fontWeight: 400, color: T.sub }}>· {s.label}</span></div>
                  <div style={{ fontSize: 12, color: T.sub, marginTop: 3 }}>{s.firstDate} ~ {s.lastDate} · {s.count}건 · 보관일 {msToDate(s.createdAt)}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => setReport(s)} style={btn(ACCENT)}>정산 리포트</button>
                  <button onClick={() => doRestore(s)} disabled={!!busy} style={btn("transparent", T.text, { border: `1px solid ${T.border}` })}>{busy === "restore:" + s.id ? "되돌리는 중..." : "되돌리기"}</button>
                </div>
              </div>
              {late > 0 && (
                <div style={{ marginTop: 10, background: WARN_SOFT, borderRadius: 10, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: WARN, lineHeight: 1.5 }}>현재 기록 중 이 시즌 기간({s.label})의 기록이 {late}건 있어요. 나중에 추가한 기록이라면 이 시즌에 합칠 수 있어요.</span>
                  <button onClick={() => doMerge(s)} disabled={!!busy} style={btn(WARN, "#1A1A1A")}>{busy === "merge:" + s.id ? "합치는 중..." : `${late}건 합치기`}</button>
                </div>
              )}
            </div>
          );
        })}
        <div style={{ fontSize: 12, color: T.sub, marginTop: 8, lineHeight: 1.6 }}>
          💡 지난 시즌에 빠진 기록이 있다면, 평소처럼 그 날짜로 기록한 뒤 여기서 "합치기"를 누르면 돼요.
        </div>
      </div>

      {/* 확인 모달 (필수 안내) */}
      {confirmOpen && (
        <Modal T={T} title={`${range.label} 초기화`} onClose={() => busy !== "reset" && setConfirmOpen(false)}>
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>기록 {target.length}건</div>
            <div style={{ fontSize: 13, color: T.sub, marginTop: 4 }}>{target[0]} ~ {target[target.length - 1]}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13, color: T.text, lineHeight: 1.6, marginBottom: 16 }}>
            {[
              ["📦", "기록은 삭제되지 않고 시즌 보관함에 영구 보관돼요."],
              ["↩️", "언제든 보관함에서 되돌릴 수 있어요. 기간 제한이 없어요."],
              ["➕", "빠진 기록이 있으면 나중에 이 시즌에 합칠 수 있어요."],
              ["📊", "초기화 후 월별 리포트와 통계는 남아 있는 기록만으로 계산돼요."],
              ["⭐", "보스 프리셋, 즐겨찾기 캐릭터, 아직 안 판 물욕템 보관함은 그대로 유지돼요."],
              ["🧾", "초기화가 끝나면 이번 시즌의 정산 리포트를 바로 보여드려요."],
            ].map(([i, t]) => <div key={t} style={{ display: "flex", gap: 10 }}><span>{i}</span><span>{t}</span></div>)}
          </div>
          <label style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13, color: T.text, cursor: "pointer", marginBottom: 12 }}>
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ width: 16, height: 16 }} />
            위 내용을 모두 확인했어요.
          </label>
          <div style={{ fontSize: 12, color: T.sub, marginBottom: 6 }}>진행하려면 아래 칸에 <b style={{ color: T.text }}>초기화</b>라고 입력해주세요.</div>
          <input value={typed} onChange={e => setTyped(e.target.value)} placeholder="초기화" style={{ ...sel, width: "100%", boxSizing: "border-box", marginBottom: 16 }} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => setConfirmOpen(false)} disabled={busy === "reset"} style={btn("transparent", T.text, { border: `1px solid ${T.border}` })}>취소</button>
            <button onClick={doReset} disabled={!canConfirm} style={btn(canConfirm ? DOWN : T.border, "#fff", { cursor: canConfirm ? "pointer" : "not-allowed" })}>
              {busy === "reset" ? "옮기는 중... (창을 닫아도 계속 진행돼요)" : "초기화 진행"}
            </button>
          </div>
        </Modal>
      )}

      {report && (
        <SeasonReport season={report} prevSeason={seasons ? seasons[seasons.findIndex(x => x.id === report.id) + 1] : null}
          T={T} isMobile={isMobile} fmt={fmt} favorites={favorites} calcIncome={calcIncome} calcSolIncome={calcSolIncome} onClose={() => setReport(null)} />
      )}
    </div>
  );
}
